import { Typography } from '@jouzen/ecom-components/v3/Typography';

import { EventType } from '@/analytics/types';
import AccessoryCard from '@/app/components/AccessoryCard';
import RichText from '@/app/components/RichText';
import useCurrency from '@/app/hooks/useCurrency';
import useIsNomadEnabled from '@/app/hooks/useIsNomadEnabled';
import useIsSteloEnabled from '@/app/hooks/useIsSteloEnabled';
import useUsaOnly from '@/app/hooks/useUsaOnly';
import { useProductsData } from '@/queries/Products';

import _accessoriesData, { NOMAD_ACCESSORY } from './_data/accessoriesData';

const AccessoriesSection = () => {
  const { currencyCode } = useCurrency();
  const { data: productsData } = useProductsData({
    includeInventory: false,
  });
  const isSteloEnabled = useIsSteloEnabled();
  const isUSAOnly = useUsaOnly();
  const isNomadEnabled = useIsNomadEnabled();

  const handleClick = (path: string) => {
    void window.ouraAnalytics.track(EventType.CTAClicked, {
      cta: 'shop',
      location: 'accessories',
      path,
      action: 'go to pdp',
    });
  };

  const accessoriesData = isNomadEnabled
    ? [NOMAD_ACCESSORY, ..._accessoriesData]
    : _accessoriesData;

  const accessoryProducts = accessoriesData
    .map(({ priceData, ...accessory }) => {
      const product = productsData?.find(
        (p) => p.productKey === accessory.productKey,
      );

      // use hardcoded price data if product is not part of catalogue (e.g. gift card)
      const accessoryPriceData =
        !product && priceData
          ? priceData
          : {
              discountedPrice: product?.discountedPrice.discountedPrice?.amount,
              basePrice: product?.price?.amount,
              currencyCode,
            };

      return {
        ...accessory,
        ...accessoryPriceData,
      };
    })
    .filter(({ productKey }) => {
      if (productKey === 'SteloDexcomCGM') {
        return isSteloEnabled;
      } else if (productKey === 'OuraGiftCard') {
        return isUSAOnly;
      } else {
        return true;
      }
    });

  return (
    <section
      className="flex flex-col items-center px-6 pt-6 md:px-15 md:pt-20"
      data-cy="accessories-section"
    >
      <Typography
        Element="h2"
        font="serif"
        className="pb-6 text-center text-3xl md:pb-4 md:text-5xl lg:text-6xl xxl:pb-16"
      >
        <RichText messageKey="accessories_to_complete_experience" />
      </Typography>
      <div className="grid grid-cols-1 md:grid-cols-2 md:gap-x-8 xxl:grid-cols-3">
        {accessoryProducts.map(({ productKey, ...accessoryProduct }) => (
          <AccessoryCard
            key={productKey}
            {...accessoryProduct}
            onClick={() => handleClick(accessoryProduct.href)}
          />
        ))}
      </div>
    </section>
  );
};

export default AccessoriesSection;
